import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Star, TicketCheck } from "lucide-react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import BookingPanel from "./BookingPanel";
import FAQAccordion from "./FAQAccordion";
import ImageGallery from "./ImageGallery";
import ListingCard from "./ListingCard";
import PackageSelector from "./PackageSelector";
import ExpandableRichTextHtml from "./ExpandableRichTextHtml";
import { get, post } from "../../utils/request";
import "./things-to-do.scss";

const hasContent = (value) => String(value || "").replace(/<[^>]+>/g, "").trim().length > 0;

const ThingsToDoDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [preSettlementResult, setPreSettlementResult] = useState(null);
  const [relatedListings, setRelatedListings] = useState([]);

  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    setLoading(true);
    setNotFound(false);
    get(`/thingsToDo/product/${id}`)
      .then((product) => {
        if (!active || !product) {
          if (active) setNotFound(true);
          return;
        }
        const firstPackage = product.packages?.find((group) => group?.skus?.length) || null;
        setListing(product);
        setSelectedPackage(firstPackage);
        setSelectedSku(firstPackage?.skus?.[0] || null);
        post("/thingsToDo/listingView", { listingId: String(product.id) }).catch(() => {});
        return get("/thingsToDo/recommendations", {
          category: product.serviceType || "All",
          limit: 4,
        }).then((result) => {
          if (active) {
            setRelatedListings((result?.products || []).filter((item) => String(item.id) !== String(product.id)).slice(0, 3));
          }
        }).catch(() => active && setRelatedListings([]));
      })
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <><Navbar /><main className="things-detail-status">Loading current experience details…</main><Footer /></>;
  }

  if (notFound || !listing) {
    return (
      <>
        <Navbar />
        <main className="things-detail-status">
          <h1>This experience is not available.</h1>
          <p>It may be unpublished or no longer active in the catalogue.</p>
          <Link to="/things-to-do">Browse Things To Do</Link>
        </main>
        <Footer />
      </>
    );
  }

  const onPackageChange = (packageName, packageObject = null) => {
    const nextPackage = packageObject || listing.packages?.find((item) => item.name === packageName) || null;
    setSelectedPackage(nextPackage);
    setSelectedSku(nextPackage?.skus?.[0] || null);
    setSelectedSlot(null);
    setPreSettlementResult(null);
  };

  const hasRating = Number(listing.rating) > 0;
  const hasSales = Number(listing.salesCount) > 0;
  const locationLabel = [listing.city, listing.country].filter(Boolean).join(", ");

  return (
    <>
      <Navbar />
      <main className="things-page things-detail-page">
        <header className="things-detail-header">
          <div className="container">
            <nav aria-label="Breadcrumb">
              <Link to="/things-to-do">Things To Do</Link><span>/</span><span>{listing.title}</span>
            </nav>
            <p className="things-detail-header__type">{listing.serviceType || "Experience"}</p>
            <h1>{listing.title}</h1>
            <div className="things-detail-header__meta">
              {locationLabel && <span><MapPin aria-hidden="true" /> {locationLabel}</span>}
              {hasRating && <span><Star aria-hidden="true" /> {listing.rating}{Number(listing.totalReviews) > 0 ? ` (${listing.totalReviews} reviews)` : ""}</span>}
              {hasSales && <span><TicketCheck aria-hidden="true" /> {listing.salesCount} booked</span>}
            </div>
          </div>
        </header>

        <div className="container things-detail-layout">
          <div className="things-detail-main">
            <ImageGallery images={listing.media || []} title={listing.title} />

            {hasContent(listing.description) && (
              <section className="things-section-card mt-3">
                <p className="things-detail-section-label">Overview</p>
                <h2 className="things-section-title">About this experience</h2>
                <ExpandableRichTextHtml html={listing.description} />
              </section>
            )}

            {hasContent(listing.highlights) && (
              <section className="things-section-card mt-3">
                <p className="things-detail-section-label">Why you&apos;ll love it</p>
                <h2 className="things-section-title">Highlights</h2>
                <ExpandableRichTextHtml html={listing.highlights} />
              </section>
            )}

            <div id="package-options" className="mt-3 things-package-section">
              <div className="things-package-section__heading">
                <p className="things-detail-section-label">Availability and prices</p>
                <h2>Choose your package</h2>
              </div>
              <PackageSelector
                listing={listing}
                selectedPackage={selectedPackage}
                selectedSku={selectedSku}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                onPackageChange={onPackageChange}
                onSkuChange={setSelectedSku}
                onPreSettlementResultChange={setPreSettlementResult}
              />
            </div>

            {hasContent(listing.whatToExpect) && (
              <section className="things-section-card mt-3">
                <p className="things-detail-section-label">Before you go</p>
                <h2 className="things-section-title">What to expect</h2>
                <ExpandableRichTextHtml html={listing.whatToExpect} />
              </section>
            )}

            {(listing.operatorName || hasContent(listing.operatorAddress) || listing.location) && (
              <section className="things-section-card mt-3">
                <p className="things-detail-section-label">Location</p>
                <h2 className="things-section-title">Operator and meeting point</h2>
                {listing.operatorName && <p><strong>{listing.operatorName}</strong></p>}
                {hasContent(listing.operatorAddress) && <div dangerouslySetInnerHTML={{ __html: listing.operatorAddress }} />}
                {listing.location && (
                  <iframe
                    title="Experience location"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(listing.location.address || listing.location.name || locationLabel)}&output=embed`}
                    className="things-location-map"
                    loading="lazy"
                  />
                )}
              </section>
            )}

            {listing.faqs?.length > 0 && (
              <section className="things-section-card mt-3">
                <p className="things-detail-section-label">Helpful information</p>
                <h2 className="things-section-title">Frequently asked questions</h2>
                <FAQAccordion faqs={listing.faqs} />
              </section>
            )}
          </div>

          <div className="things-detail-sidebar">
            <div className="things-sticky-panel">
              <BookingPanel
                listing={listing}
                selectedSku={selectedSku}
                preSettlementResult={preSettlementResult}
              />
            </div>
          </div>
        </div>

        {relatedListings.length > 0 && (
          <section className="container things-related-section">
            <div className="things-section-heading">
              <div><p>Keep exploring</p><h2 className="things-section-title">You might also like</h2></div>
            </div>
            <div className="row g-3">
              {relatedListings.map((item) => <div className="col-md-6 col-lg-4" key={item.id}><ListingCard listing={item} /></div>)}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

export default ThingsToDoDetail;
